import { FC } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'

import type { Space } from 'features/space/types/spaceDetail'

import { SafeLinkifiedText } from './SafeLinkifiedText'
import space_styles from './SocialSection.module.scss'

type Props = {
  space: Space
  isLocker?: boolean
}

const isKyotoSpace = (space: Space) =>
  space.prefecture_id === 26 || space.prefecture_slug === 'kyoto'

/** SNS リンク + 備考セクション。アカウント名のみの場合は各サービスの URL に正規化する。 */
export const SocialSection: FC<Props> = ({ space, isLocker }) => {
  const { t } = useTranslation()

  const socialLinks = [
    {
      link: space.fb_account?.match('://')
        ? space.fb_account
        : 'https://www.facebook.com/' + space.fb_account,
      isPermit: !!space.fb_account?.length,
      icon: '/facebook_icon.svg',
      meta: t('META.FACEBOOK_ICON'),
    },
    {
      link: space.tw_account?.match('://')
        ? space.tw_account
        : 'https://twitter.com/' + space.tw_account,
      isPermit: !!space.tw_account?.length,
      icon: '/twitter_icon.svg',
      meta: t('META.TWITTER_ICON'),
    },
    {
      link: space.insta_account?.match('://')
        ? space.insta_account
        : 'https://www.instagram.com/' + space.insta_account,
      isPermit: !!space.insta_account?.length,
      icon: '/instagram_icon.svg',
      meta: t('META.INSTAGRAM_ICON'),
    },
  ]

  // 京都のスペースはホームページをテキストリンクで表示するため、web アイコンは京都以外のみ
  if (!isKyotoSpace(space)) {
    socialLinks.unshift({
      link: space.homepage_url,
      isPermit: !!space.homepage_url?.length,
      icon: '/web_icon.svg',
      meta: t('META.WEB_LINK_ICON'),
    })
  }

  return (
    <section className={space_styles.spaceInformationSection}>
      <h2 className={space_styles.spaceInformationSectionHeadingTitle}>
        {t(
          isLocker
            ? 'LOCKER.LABEL.LOCKER_INFORMATION'
            : 'SPACE_DETAIL.SECTION.SPACE_INFORMATION.TITLE'
        )}
      </h2>
      <div className={space_styles.social}>
        <div className={space_styles.socialSectionLabel}>
          <img src="/social_icon.svg" alt="" height="24px" width="24px" />
          <p className={space_styles.labelName}>
            {t('SPACE_DETAIL.SECTION.SPACE_INFORMATION.SOCIAL')}
          </p>
        </div>
        <div className={space_styles.socialLinkContainer}>
          {isKyotoSpace(space) && space.homepage_url && space.homepage_url.length > 0 && (
            <Link href={space.homepage_url} passHref>
              <a className={space_styles.ecboDeliverLink} target="_blank" rel="noreferrer">
                {space.homepage_url}
              </a>
            </Link>
          )}
          <ul className={space_styles.socialList}>
            {socialLinks.map((socialLink, index) => (
              <li
                className={`${space_styles.socialIcon} ${
                  !socialLink.isPermit ? space_styles.disabledSocialLink : ''
                }`}
                key={index}
              >
                {socialLink.isPermit ? (
                  <Link href={socialLink.link} passHref>
                    <a target="_blank" rel="noreferrer">
                      <img src={socialLink.icon} alt={socialLink.meta} height="24px" width="24px" />
                    </a>
                  </Link>
                ) : (
                  <img src={socialLink.icon} alt={socialLink.meta} height="24px" width="24px" />
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className={space_styles.sectionContents}>
        {space.extra_info && (
          <div className={space_styles.note}>
            <div className={space_styles.noteSectionLabel}>
              <img className={space_styles.img} src="/notes_icon.svg" alt="" />
              <p className={space_styles.label}>
                {t('SPACE_DETAIL.SECTION.SPACE_INFORMATION.NOTE')}
              </p>
            </div>
            <p className={space_styles.labelName}>
              <SafeLinkifiedText>{space.extra_info}</SafeLinkifiedText>
            </p>
          </div>
        )}
      </div>
    </section>
  )
}
